<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useWahaQr } from '../useWahaQr';
import { WHATSAPP_STAGES } from '../visorSteps';
import { BTN_SECONDARY, BTN_SMALL, CARD, CHIP, CHIP_TONES, MOTION, SMALL, TILE } from '../ui';

// WhatsApp por QR com estado ao vivo. Conta que já tem caixa vê o passo feito.
const props = defineProps({
  accountId: { type: [Number, String], required: true },
  done: { type: Boolean, default: false },
  pendingInboxId: { type: Number, default: null },
  inboxName: { type: String, required: true },
  phone: { type: String, default: '' },
});
const emit = defineEmits(['inboxCreated', 'connected']);

const { t } = useI18n();
const { qrCode, stage, error, secondsLeft, start } = useWahaQr({
  accountId: computed(() => props.accountId),
  onInboxCreated: id => emit('inboxCreated', id),
  onConnected: id => emit('connected', id),
});

const begin = () =>
  start({ existingInboxId: props.pendingInboxId, inboxName: props.inboxName });

onMounted(() => {
  if (!props.done) begin();
});

const connected = computed(() => props.done || stage.value === 'conectado');

const chip = computed(() => {
  if (connected.value) return { tone: CHIP_TONES.ok, key: 'conectado' };
  if (error.value) return { tone: CHIP_TONES.aviso, key: 'erro' };
  if (stage.value === 'aguardando') return { tone: CHIP_TONES.aviso, key: 'aguardando' };
  if (stage.value === 'preparando') return { tone: CHIP_TONES.neutro, key: 'preparando' };
  return { tone: CHIP_TONES.azul, key: stage.value };
});

const stageClass = name => {
  const current = WHATSAPP_STAGES.indexOf(stage.value);
  const index = WHATSAPP_STAGES.indexOf(name);
  if (connected.value || index < current) return 'bg-n-teal-3 text-n-teal-11';
  if (index === current) return 'bg-n-blue-3 text-n-blue-11';
  return 'bg-n-slate-3 text-n-slate-10';
};

const qrCaption = computed(() => {
  if (stage.value === 'aguardando') return t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.NEW_CODE', { s: secondsLeft.value });
  if (stage.value === 'preparando') return t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.PREPARING');
  return t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.KEEP_OPEN');
});
</script>

<template>
  <section :class="CARD" data-testid="whatsapp-card">
    <div class="flex items-center gap-3 mb-4 flex-wrap">
      <span :class="TILE" class="bg-n-teal-3 text-[#25D366]">
        <Icon icon="i-ri-whatsapp-fill" />
      </span>
      <h2 class="text-xl font-medium tracking-[-0.02em]">
        {{ t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.TITLE') }}
      </h2>
      <span :class="[CHIP, chip.tone]" data-testid="whatsapp-estado" aria-live="polite">
        <i class="size-2 rounded-full bg-current" :class="connected ? '' : 'motion-safe:animate-pulse'" />
        {{ t(`ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.STAGES.${chip.key.toUpperCase()}`) }}
      </span>
    </div>

    <div v-if="stage === 'conectado'" class="flex items-center gap-3 text-n-slate-11">
      <Icon icon="i-ph-check-circle-duotone" class="size-6 text-n-teal-11" />
      {{ t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.CONNECTED') }}
    </div>

    <p v-else-if="done" class="text-n-slate-11" data-testid="whatsapp-feito">
      {{ t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.ALREADY') }}
    </p>

    <div v-else class="grid gap-7 md:grid-cols-[13.75rem_1fr] items-start">
      <div class="text-center max-md:max-w-[13.75rem] max-md:mx-auto w-full">
        <div class="aspect-square bg-white border border-n-weak rounded-2xl p-3.5 grid place-items-center">
          <img v-if="qrCode" :src="qrCode" :alt="t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.QR_ALT')" class="w-full" data-testid="whatsapp-qr" />
          <Spinner v-else class="text-n-slate-10" />
        </div>
        <p class="mt-2" :class="SMALL">{{ qrCaption }}</p>
      </div>
      <div>
        <ol class="grid gap-3.5">
          <li v-for="(item, index) in ['OPEN', 'DEVICES', 'SCAN']" :key="item" class="flex gap-3 items-center text-n-slate-11">
            <span :class="TILE" class="bg-n-blue-3 text-n-blue-11">
              <Icon :icon="['i-ph-device-mobile-duotone', 'i-ph-link-duotone', 'i-ph-qr-code-duotone'][index]" />
            </span>
            {{ t(`ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.HOW.${item}`, { phone: phone || t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.THE_NUMBER') }) }}
          </li>
        </ol>
        <div class="flex gap-1.5 mt-5 flex-wrap">
          <span
            v-for="name in WHATSAPP_STAGES"
            :key="name"
            class="flex items-center gap-1.5 text-[0.8125rem] px-2.5 py-1.5 rounded-md"
            :class="[MOTION, stageClass(name)]"
          >
            <i class="size-2 rounded-full bg-current" />
            {{ t(`ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.STAGES.${name.toUpperCase()}`) }}
          </span>
        </div>
        <div v-if="error" class="mt-4 flex items-center gap-3 flex-wrap">
          <span class="text-sm text-n-amber-11">{{ t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.ERROR') }}</span>
          <button type="button" :class="[BTN_SECONDARY, BTN_SMALL]" @click="begin">
            {{ t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.RETRY') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
